import { create } from 'zustand';
import {
  CampaignCreationStore,
  CampaignCreationStoreState
} from 'modules/campaign-creation/stores/CampaignCreationStore';
import { CampaignType, Channels } from 'generated-graphql/types';
import { getShouldShowAutomationStep } from 'modules/campaign-creation/utils/getShouldShowAutomationStep';
import { getRecipientsType } from 'modules/campaign-creation/steps/CampaignCreationChooseRecipientsStep';
import {
  getCampaignTypeFromRecipientType,
  getRecipentStepNextStep,
  getRecipentStepPrevStep
} from 'modules/campaign-creation/utils/getRecipentStepNextPrevStep';
import { handleStoreSaveCampaign } from 'modules/campaign-creation/utils/handleStoreSaveCampaign';
import { CampaignCreationStep } from './CampaignCreationSteps';

type GetPrevStateValuesProps = {
  prevState: CampaignCreationStore['state'] | null;
};

const getPrevStateValues = ({
  prevState
}: GetPrevStateValuesProps): CampaignCreationStoreState => ({
  campaignType: prevState?.campaignType || CampaignType.NewCandidateOutreach,
  engagementDescriptionState: prevState?.engagementDescriptionState || null,
  noATSJobDescriptionState: prevState?.noATSJobDescriptionState || null,
  ATSJobDescriptionState: prevState?.ATSJobDescriptionState || null,
  jobDescriptionParsedState: prevState?.jobDescriptionParsedState || null,
  questions: prevState?.questions || null,
  ATSImportState: prevState?.ATSImportState || null,
  CSVImportState: prevState?.CSVImportState || null,
  channel: prevState?.channel || null,
  // ?? not || — an explicit false must survive state updates
  emailChannelFallbackEnabled: prevState?.emailChannelFallbackEnabled ?? null,
  language: prevState?.language || null,
  setupAndSimulationFields: prevState?.setupAndSimulationFields || null,
  automationTasks: prevState?.automationTasks || null,
  launchConfig: prevState?.launchConfig || null
});

type UpdateStateProps = {
  campaignId: string | null;
  prev: CampaignCreationStoreState | null | undefined;
  updates: Partial<CampaignCreationStoreState>;
};

const updateState = ({ campaignId, prev, updates }: UpdateStateProps) => {
  handleStoreSaveCampaign(campaignId, {
    ...updates,
    campaignType: updates.campaignType || prev?.campaignType
  });

  const returnValue = {
    ...getPrevStateValues({ prevState: prev || null }),
    ...updates
  };

  return returnValue;
};

const getStepAfterCandidateSource = (
  state: CampaignCreationStoreState | null,
  isFromAnalysisFlow: boolean
): CampaignCreationStep => {
  // Engagement outreach never needs the candidate application check
  if (state?.campaignType === CampaignType.EngagementOutreach) {
    return 'CHANNEL';
  }
  // ATS list imports skip CANDIDATE_APPLICATION — their WhatsApp visibility
  // is controlled by the allowWhatsAppForAtsListCampaigns org config at the CHANNEL step
  // Guard with !isFromAnalysisFlow to avoid skipping for stale state during back-navigation
  if (!isFromAnalysisFlow && state?.ATSImportState?.atsListId) {
    return 'CHANNEL';
  }
  // Analysis flow always needs to ask if candidates applied
  if (isFromAnalysisFlow) {
    return 'CANDIDATE_APPLICATION';
  }
  // If user has an ATS job selected, candidates have applied (warm outreach)
  if (state?.ATSJobDescriptionState?.selectedJob?.atsJobId) {
    return 'CHANNEL';
  }
  // Otherwise, we need to ask
  return 'CANDIDATE_APPLICATION';
};

export const useCampaignCreationStore = create<CampaignCreationStore>()(
  (set, get) => ({
    campaignId: null,
    campaignLastUpdatedAt: null,
    step: 'CHOOSE_TYPE',
    state: null,
    onNewCampaign: () => {
      set({
        campaignId: null,
        campaignLastUpdatedAt: null,
        step: 'CHOOSE_TYPE',
        state: null
      });
    },
    onEditCampaign: (values) => {
      const isEngagement =
        values.state.campaignType === CampaignType.EngagementOutreach;
      const isDuplicate = values.state.isDuplicate;

      let step: CampaignCreationStep = 'EDIT_JOB_DESCRIPTION';

      if (isEngagement) {
        step = 'QUESTIONS_AND_DOCUMENTS';
      }

      if (isDuplicate) {
        step = 'CHOOSE_TYPE';
      }

      set({
        campaignId: values.campaignId,
        campaignLastUpdatedAt: null,
        step,
        state: values.state
      });
    },
    onChooseTypeSubmit: (values) => {
      set((prevState) => {
        if (values.campaignType === CampaignType.EngagementOutreach) {
          return {
            step: 'ENGAGEMENT_DESCRIPTION',
            state: updateState({
              campaignId: get().campaignId,
              prev: prevState?.state,
              updates: {
                campaignType: values.campaignType,
                noATSJobDescriptionState: null,
                ATSJobDescriptionState: null,
                jobDescriptionParsedState: null
              }
            })
          };
        }

        return {
          step: 'JOB_DESCRIPTION',
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates: {
              campaignType: values.campaignType,
              engagementDescriptionState: null
            }
          })
        };
      });
    },
    onEngagementDescriptionSubmit: (values) =>
      set((prevState) => ({
        step: 'AI_GENERATE_JOB_DESCRIPTION_AND_QUESTIONS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            engagementDescriptionState: values
          }
        })
      })),
    onEngagementDescriptionBack: () =>
      set(() => ({
        step: 'CHOOSE_TYPE'
      })),
    onNoATSJobDescriptionSubmit: (values) =>
      set((prevState) => ({
        step: 'AI_GENERATE_JOB_DESCRIPTION_AND_QUESTIONS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            noATSJobDescriptionState: values,
            ATSJobDescriptionState: null
          }
        })
      })),
    onATSJobDescriptionSubmit: (values) =>
      set((prevState) => ({
        step: 'AI_GENERATE_JOB_DESCRIPTION_AND_QUESTIONS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            ATSJobDescriptionState: {
              selectedJob: values
            },
            noATSJobDescriptionState: null
          }
        })
      })),
    onJobDescriptionBack: () =>
      set(() => ({
        step: 'CHOOSE_TYPE'
      })),
    onAIGenerateJobDescription: (values) =>
      set((prevState) => ({
        step: 'AI_GENERATE_JOB_DESCRIPTION_AND_QUESTIONS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            jobDescriptionParsedState: {
              jobDescriptionParsed: values.jobDescriptionParsed,
              additionalContext: null
            }
          }
        })
      })),
    onAIGenerateQuestions: (values) =>
      set((prevState) => ({
        step: 'AI_GENERATE_JOB_DESCRIPTION_AND_QUESTIONS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            questions: values.questions
          }
        })
      })),
    onAIGenerateJobDescriptionAndQuestionsFinished: (values) =>
      set((prevState) => {
        const isEngagement =
          prevState?.state?.campaignType === CampaignType.EngagementOutreach;

        if (isEngagement) {
          return {
            step: 'QUESTIONS_AND_DOCUMENTS',
            campaignId: values.campaignId,
            campaignLastUpdatedAt: new Date()
          };
        }

        return {
          step: 'EDIT_JOB_DESCRIPTION',
          campaignId: values.campaignId,
          campaignLastUpdatedAt: new Date()
        };
      }),
    onEditJobDescription: async (values) =>
      set((prevState) => {
        if (prevState?.state?.ATSJobDescriptionState?.selectedJob) {
          return {
            state: updateState({
              campaignId: get().campaignId,
              prev: prevState?.state,
              updates: {
                ATSJobDescriptionState: {
                  selectedJob: {
                    ...prevState?.state?.ATSJobDescriptionState?.selectedJob,
                    jobDescriptionText: values.jobDescriptionText
                  }
                }
              }
            })
          };
        } else {
          return {
            state: updateState({
              campaignId: get().campaignId,
              prev: prevState?.state,
              updates: {
                noATSJobDescriptionState: {
                  externalId:
                    prevState?.state?.noATSJobDescriptionState?.externalId ||
                    null,
                  jobDescriptionText: values.jobDescriptionText
                }
              }
            })
          };
        }
      }),
    onFinishEditingJobDescription: async (values) => {
      set((prevState) => {
        return {
          step: 'QUESTIONS_AND_DOCUMENTS',
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates: {
              isDuplicate: false,
              jobDescriptionParsedState: {
                jobDescriptionParsed: values.jobDescriptionParsed,
                additionalContext: values.additionalContext
              }
            }
          })
        };
      });
    },
    onEditJobDescriptionBack: () =>
      set(() => ({
        step: 'JOB_DESCRIPTION'
      })),
    onQuestionsAndDocumentsSubmit: async (values) =>
      set((prevState) => ({
        step: 'CHOOSE_RECIPIENTS',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            questions: values.questions
          }
        })
      })),
    onQuestionsAndDocumentsBack: () =>
      set((prevState) => {
        const isEngagement =
          prevState?.state?.campaignType === CampaignType.EngagementOutreach;

        if (isEngagement) {
          return {
            step: 'ENGAGEMENT_DESCRIPTION'
          };
        }

        return {
          step: 'EDIT_JOB_DESCRIPTION'
        };
      }),
    onChooseRecipientsSubmit: (values) => {
      const nextStep = getRecipentStepNextStep(values.recipientType);

      if (values.recipientType === 'everyone') {
        return set((prevState) => {
          const updates = {
            ATSImportState: null,
            CSVImportState: null,
            campaignType: getCampaignTypeFromRecipientType({
              previousCampaignType: prevState?.state?.campaignType,
              recipientType: values.recipientType
            })
          };
          return {
            step: getStepAfterCandidateSource(
              {
                ...getPrevStateValues({
                  prevState: prevState?.state || null
                }),
                ...updates
              },
              false
            ),
            state: updateState({
              campaignId: get().campaignId,
              prev: prevState?.state,
              updates
            })
          };
        });
      }

      if (values.recipientType === 'specific') {
        return set((prevState) => ({
          step: getRecipentStepNextStep(values.recipientType),
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates: {
              campaignType: getCampaignTypeFromRecipientType({
                previousCampaignType: prevState?.state?.campaignType,
                recipientType: values.recipientType
              })
            }
          })
        }));
      }

      if (values.recipientType === 'imported') {
        return set((prevState) => ({
          step: getRecipentStepNextStep(values.recipientType),
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates: {
              campaignType: getCampaignTypeFromRecipientType({
                previousCampaignType: prevState?.state?.campaignType,
                recipientType: values.recipientType
              })
            }
          })
        }));
      }

      return set({
        step: nextStep
      });
    },
    onChooseRecipientsBack: () =>
      set({
        step: 'QUESTIONS_AND_DOCUMENTS'
      }),
    onChooseAnalysisSubmit: () =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(prevState?.state || null, true),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            ATSImportState: null,
            CSVImportState: null
          }
        })
      })),
    onAnalysisSetupBack: () =>
      set({
        step: 'CHOOSE_RECIPIENTS'
      }),
    onAnalysisSetupSkip: () =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(prevState?.state || null, true),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            ATSImportState: null,
            CSVImportState: null
          }
        })
      })),
    onChooseAnalysisSkip: () =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(prevState?.state || null, true),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            ATSImportState: null,
            CSVImportState: null
          }
        })
      })),
    onATSImportCandidates: async (values) =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(
          {
            ...getPrevStateValues({ prevState: prevState?.state || null }),
            ATSImportState: values,
            CSVImportState: null
          },
          false
        ),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            ATSImportState: values,
            CSVImportState: null
          }
        })
      })),
    onCSVImportCandidates: async (values) =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(
          {
            ...getPrevStateValues({ prevState: prevState?.state || null }),
            CSVImportState: values,
            ATSImportState: null
          },
          false
        ),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            CSVImportState: values,
            ATSImportState: null
          }
        })
      })),
    onCSVImportSkip: () =>
      set((prevState) => ({
        step: getStepAfterCandidateSource(
          {
            ...getPrevStateValues({ prevState: prevState?.state || null }),
            CSVImportState: null,
            ATSImportState: null
          },
          false
        ),
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            CSVImportState: null,
            ATSImportState: null
          }
        })
      })),
    onImportCandidatesBack: () =>
      set(() => ({
        step: 'CHOOSE_RECIPIENTS'
      })),
    onChannelSubmit: async (values) =>
      set((prevState) => {
        const updates: Record<string, any> = {
          channel: values.channel,
          emailChannelFallbackEnabled: values.emailChannelFallbackEnabled
        };

        if (
          values.channel === Channels.Email &&
          prevState?.state?.channel !== Channels.Email
        ) {
          updates.setupAndSimulationFields = {
            ...prevState?.state?.setupAndSimulationFields,
            templateId: null
          };
        }

        return {
          step: 'SETUP_AND_SIMULATION',
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates
          })
        };
      }),
    onChannelBack: (values) =>
      set((prevState) => {
        if (values.shouldGoToCandidateApplication) {
          return {
            step: 'CANDIDATE_APPLICATION'
          };
        }
        const recipientsType = getRecipientsType(
          prevState?.state,
          values.selectedCandidateBatchStatuses
        );
        const prevStep = getRecipentStepPrevStep(recipientsType);
        return {
          step: prevStep
        };
      }),
    onChannelBackToCSVUpload: () =>
      set(() => ({
        step: 'IMPORT_CANDIDATES'
      })),
    onCandidateApplicationSubmit: (values) =>
      set((prevState) => ({
        step: 'CHANNEL',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            campaignType: values.campaignType,
            // Clear channel when switching to cold outreach to prevent
            // a previously selected WhatsApp from persisting
            ...(values.campaignType === CampaignType.NewCandidateOutreach &&
            prevState?.state?.channel === Channels.Whatsapp
              ? { channel: null }
              : {})
          }
        })
      })),
    onCandidateApplicationBack: (values) =>
      set((prevState) => {
        const recipientsType = getRecipientsType(
          prevState?.state,
          values.selectedCandidateBatchStatuses
        );
        const prevStep = getRecipentStepPrevStep(recipientsType);
        return {
          step: prevStep
        };
      }),
    onSetupAndSimulationSubmit: async (values) =>
      set((prevState) => {
        const shouldShowAutomationStep = getShouldShowAutomationStep({
          hasATSJobID: !!prevState?.state?.ATSJobDescriptionState?.selectedJob,
          hasATSListID: !!prevState?.state?.ATSImportState?.atsListId,
          questions: prevState?.state?.questions || []
        });

        // Only advance to AUTOMATIONS step if needed
        // If not needed, stay on current step - the launch buttons handle navigation
        return {
          step: shouldShowAutomationStep ? 'AUTOMATIONS' : prevState.step,
          state: updateState({
            campaignId: get().campaignId,
            prev: prevState?.state,
            updates: {
              setupAndSimulationFields: values
            }
          })
        };
      }),
    onSetupAndSimulationBack: () =>
      set(() => ({
        step: 'CHANNEL'
      })),
    // Saves automation tasks and advances to CREATE step (for CSV/non-launch flows)
    onAutomationTasksSubmit: (values) =>
      set((prevState) => ({
        step: 'CREATE',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            automationTasks: values.automationTasks
          }
        })
      })),
    // Saves automation tasks + launch config and advances to CREATE step (for launch/schedule flows)
    onAutomationTasksSubmitWithLaunchConfig: (values) =>
      set((prevState) => ({
        step: 'CREATE',
        state: updateState({
          campaignId: get().campaignId,
          prev: prevState?.state,
          updates: {
            automationTasks: values.automationTasks,
            launchConfig: values.launchConfig
          }
        })
      })),
    onAutomationTasksBack: () =>
      set(() => ({
        step: 'SETUP_AND_SIMULATION'
      })),
    onChangeUpdateDate: () =>
      set(() => ({
        campaignLastUpdatedAt: new Date()
      })),
    onCampaignCreateError: () =>
      set(() => ({
        step: 'SETUP_AND_SIMULATION'
      })),
    setLanguage: (language: string) =>
      set((prevState) => ({
        state: prevState.state
          ? { ...prevState.state, language }
          : prevState.state
      }))
  })
);
